import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';

import { useDispatch, useSelector } from 'react-redux';
import { NavigationService } from '../services/navigation.service';
import { GlobalService } from '../services/global.service';

import { UserService } from '../services/user.service';
import { ClientService } from '../services/client.service';
import { SiteService } from '../services/site.service';
import { ToastService } from 'services/toast.service';

import { ROUTES, SITE_ID } from '../constants/navigation.constants';
import { MAX_LENGTH_ENTITY } from 'constants/limite.constants';

import { Client, CommonGlobalService, MultiSelect, Option, RootState, Site, UserFilter, UserLinked } from '@umanforge/common';
import { ModifySite } from 'interfaces/api_site.interfaces';

import { HomeTopBar } from '../components/menu/home_top_bar.component';
import { Breadcrumb } from '../components/menu/breadcrumb.component';
import { Button } from '../components/inputs/button.component';
import { Input } from '../components/inputs/input.component';
import { BodyConfirmModal } from '../components/modals/body/body_confirm_modal.component';
import { Select } from '../components/inputs/select.component';
import { Loading } from '../components/loading.component';
import { SwitchAdminUser } from 'components/users/switch_admin_user.component';
import { ErrorModal } from 'components/modals/error_modal.component';
import { HomeLeftBar } from 'components/menu/home_left_bar.component';

import { ClientsIcon } from '../components/icons/clients_icon.component';
import { SitesIcon } from '../components/icons/sites_icon.component';
import { UsersIcon } from '../components/icons/users_icon.component';
import { SaveIcon } from '../components/icons/save_icon.component';
import { CountryIcon } from '../components/icons/country_icon.component';
import { CityIcon } from '../components/icons/city_icon.component';
import { ZipCodeIcon } from '../components/icons/zip_code_icon.component';
import { AddressIcon } from '../components/icons/address_icon.component';
import { BinIcon } from '../components/icons/bin_icon.component';
import { SectorsIcon } from 'components/icons/sectors_icon.component';
import { SftpSiteSection } from 'features/sftp_site/components/sftp_site_section.component';
import { SftpService } from 'services/sftp.service';

const toastService: ToastService = new ToastService( 'Details Site' );
const defaultChangedUsers: { added: UserLinked[], removed: UserLinked[] } = { added: [], removed: [] };


export function DetailsSiteView() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();

  const token = useSelector( ( state: RootState ) => { return state.token.value; } );
  const me = useSelector( ( state: RootState ) => { return state.user.value; } );
  const dispatch = useDispatch();

  /*----------------------------- useState ------------------------------*/
  const [ site, setSite ] = useState<Site | null>( null );
  const [ clientOfSite, setClientOfSite ] = useState<Option<Client> | undefined>();

  const [ modifySite, setModifySite ] = useState<ModifySite>( {} );
  const [ changedAdmins, setChangedAdmins ] = useState<{ added: UserLinked[], removed: UserLinked[] }>( defaultChangedUsers );
  const [ defaultUsers, setDefaultUsers ] = useState<Option<UserLinked>[]>( [] );
  const [ defaultAdmins, setDefaultAdmins ] = useState<Option<UserLinked>[]>( [] );
  const [ selectedAdmins, setSelectedAdmins ] = useState<Option<UserLinked>[]>( [] );
  const [ defaultSftpStatus, setDefaultSftpStatus ] = useState<boolean>( false );
  const [ selectedSftpStatus, setSelectedSftpStatus ] = useState<boolean>( false );

  const [ newEmails, setNewEmails ] = useState<string[]>( [] );
  const [ removedEmails, setRemovedEmails ] = useState<string[]>( [] );

  const [ canPut, setCanPut ] = useState<boolean>( false );
  const [ hasChanged, setHasChanged ] = useState<boolean>( false );
  const [ isAdminOfSite, setIsAdminOfSite ] = useState<boolean>( false );
  const [ confirmDeleteModalIsOpen, setConfirmDeleteModalIsOpen ] = useState( false );

  const [ isLoading, setIsLoading ] = useState<boolean>( false );

  /*---------------------------- useEffect ------------------------------*/
  useEffect( () => {
    if ( me ) {
      refreshSite();
    }
  }, [ me ] );

  useEffect( () => {
    if ( site ) {
      checkCanPutSite();
    }
  }, [ modifySite, changedAdmins, defaultSftpStatus, selectedSftpStatus, removedEmails, newEmails ] );

  useEffect( () => {
    const siteUsers = defaultUsers
      .filter( user => { return defaultAdmins.find( admin => { return user.object.id === admin.object.id; } ) == null; } )
      .concat( defaultAdmins );
    const items = {
      added: selectedAdmins
        .map( item => { return item.object; } )
        .filter( selectedAdmin => {
          return (
            ( !siteUsers.find( siteUser => { return ( siteUser.object.id === selectedAdmin.id ); } ) && selectedAdmin.isAdmin === true ) ||
            ( siteUsers.find( siteUser => { return ( siteUser.object.id === selectedAdmin.id && ( siteUser.object.isAdmin === false || siteUser.object.isAdmin == null ) ) && selectedAdmin.isAdmin === true; } ) )
          );
        } ),
      removed: siteUsers
        .map( item => { return item.object; } )
        .filter( siteUser => {
          return !selectedAdmins.find( selectedAdmin => { return selectedAdmin.object.id === siteUser.id; } ) && siteUser.isAdmin === true;
        } )
    };
    setChangedAdmins( items );
  }, [ selectedAdmins ] );

  /*------------------------- Requetes API User -------------------------*/
  const refreshSite = async () => {
    setSite( null );
    setIsLoading( true );
    if ( params.siteId ) {
      const refreshedSite = await SiteService.getSite( params.siteId, token, dispatch, () => { NavigationService.toSites( navigate, false ); } );
      const refreshedClient = await ClientService.getClient( refreshedSite.clientId, token, dispatch, () => { NavigationService.toSites( navigate, false ); } );
      let isAdmin = false;
      if ( me ) {
        isAdmin = CommonGlobalService.isAdminOfSite( me, refreshedSite.id ) || CommonGlobalService.isAdminOfClient( me, refreshedSite.clientId );
      }
      setIsAdminOfSite( isAdmin );
      Promise.all( [
        UserService.getUsersOfSites( [ params.siteId ], token, dispatch, () => { NavigationService.toClients( navigate, false ); } ),
        UserService.getAdminsOfSite( params.siteId, token, dispatch, () => { NavigationService.toClients( navigate, false ); } ),
      ] )
        .then( responses => {
          const admins = ( responses[ 1 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, true ); } );
          const users = ( responses[ 0 ] ?? [] ).map( user => { return GlobalService.toUserLinked( user, false ); } );

          const usersOption = CommonGlobalService.toOptions( users, isAdmin, !isAdmin );
          const adminsOption = CommonGlobalService.toOptions( admins, isAdmin, !isAdmin )
            .map( user => {
              const admin = ( responses[ 1 ] ?? [] ).find( adm => { return adm.id === user.object.id; } );
              let isHigherAdmin = false;
              if ( admin ) {
                const clientOfUser = admin.clients.find( client => { return client.id === refreshedClient.id; } );
                isHigherAdmin = ( clientOfUser != null && clientOfUser.isAdmin == true );
              }
              return {
                ...user,
                isDisabled: isHigherAdmin
              };
            } );

          setDefaultUsers( usersOption );
          setDefaultAdmins( structuredClone( adminsOption ) );
          setSelectedAdmins( structuredClone( adminsOption ) );

          setNewEmails( [] );
          setRemovedEmails( [] );

          setSite( refreshedSite );
          setClientOfSite( CommonGlobalService.toOptions( [ refreshedClient ] )[ 0 ] );
        } );
      setIsLoading( false );
    }
  };

  const deleteSite = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      setIsLoading( true );
      let isDeleted = false;
      if ( params.siteId ) {
        isDeleted = await SiteService.deleteSite( params.siteId, token, dispatch, refreshSite, refreshSite );
      }
      if ( isDeleted ) {
        toastService.showInfo( t( 'The {0} site has been delete.' ).replace( '{0}', ( site?.name ?? t( 'The site' ) ) ) );
        NavigationService.toSites( navigate, false );
      }
      setIsLoading( false );
      resolve( isDeleted );
    } );
  };

  const putSite = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isSiteChange: boolean =  modifySite.name != null && modifySite.name != '' && modifySite.name != site?.name ||
        modifySite.address != null && modifySite.address != site?.address ||
        modifySite.additionalAddress != null && modifySite.additionalAddress != site?.additionalAddress ||
        modifySite.city != null && modifySite.city != site?.city ||
        modifySite.country != null && modifySite.country != site?.country ||
        modifySite.zipCode != null && modifySite.zipCode != site?.zipCode;
      if ( params.siteId && isSiteChange ) {
        isUpdated = await SiteService.putSite( params.siteId, modifySite, token, dispatch, refreshSite, refreshSite );
      }
      resolve( isUpdated );
    } );
  };

  const putSftpConfig = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      const isSftpChange: boolean = ( modifySite.sftpPublicKey != null && modifySite.sftpPublicKey != site?.sftpPublicKey ) || selectedSftpStatus != defaultSftpStatus;
      if ( params.siteId && isSftpChange ) {
        isUpdated = await SftpService.managedSftpOfSite( params.siteId, selectedSftpStatus, selectedSftpStatus ? modifySite.sftpPublicKey : undefined, token, dispatch, refreshSite, refreshSite );
      }
      resolve( isUpdated );
    } );
  };

  const managedEmailsForSftpError = (): Promise<boolean> => {
    return new Promise ( async resolve => {
      let isUpdated = false;
      if ( params.siteId ) {
        if ( removedEmails.length > 0 ) {
          isUpdated = await SftpService.removeEmailForErrorSftpForSite( params.siteId, removedEmails, token, dispatch, refreshSite, refreshSite );
        }
        if ( newEmails.length > 0 ) {
          isUpdated = await SftpService.addEmailForErrorSftpForSite( params.siteId, newEmails, token, dispatch, refreshSite, refreshSite ) || isUpdated;
        }
      }
      resolve( isUpdated );
    } );
  };

  const addAdminsToSite = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( site?.id && changedAdmins.added.length > 0 ) {
        const body = { admins: changedAdmins.added.map( admin => { return admin.id; } ) };
        isUpdated = await SiteService.postAdminsToSite( site?.id, body, token, dispatch, refreshSite, refreshSite );
      }
      resolve( isUpdated );
    } );
  };

  const removedAdminsToSite = (): Promise<boolean> => {
    return new Promise( async resolve => {
      let isUpdated = false;
      if ( site?.id && changedAdmins.removed.length > 0 ) {
        const body = { admins: changedAdmins.removed.map( admin => { return admin.id; } ) };
        isUpdated = await SiteService.deleteAdminsToSite( site?.id, body, token, dispatch, refreshSite, refreshSite );
      }
      resolve( isUpdated );
    } );
  };

  const getUsers = async ( filter: UserFilter ): Promise<{ total: number, list: Option<UserLinked>[] }> => {
    if ( filter.filterName === undefined || filter.filterName === '' ) {
      delete filter.filterName;
    }
    if ( clientOfSite != null ) {
      filter.filterClients = [ clientOfSite.value ];
    }

    const result = ( await UserService.getAllowedUsers( filter as UserFilter, token, dispatch ) );
    return { total: result.total, list: CommonGlobalService.toOptions<UserLinked>(
      result.users.map<UserLinked>( user => {
        return {
          id: user.id,
          fullname: user.fullname,
          isDisable: user.isDisable,
          isAdminOfUser: user.isAdminOfUser,
          isAdmin: true,
        };
      } ) )
    };
  };

  /*---------------------------- Functions ------------------------------*/
  const onSaveClick = async () => {
    let isUserModify = false;
    setIsLoading( true );
    if ( site ) {
      const isPutSite = await putSite();
      const isPutSftpConfig = await putSftpConfig();
      const isEmailSftpError = await managedEmailsForSftpError();
      const isAddAdminsToSite = await addAdminsToSite();
      const isRemovedAdminsToSite = await removedAdminsToSite();
      isUserModify = isPutSite ||
        isPutSftpConfig ||
        isEmailSftpError ||
        isAddAdminsToSite ||
        isRemovedAdminsToSite;
    }
    if ( isUserModify ) {
      toastService.showInfo( t( 'The {0} site has been modify.' ).replace( '{0}', ( site?.name ?? t( 'The site' ) ) ) );
      refreshSite();
    }
    setIsLoading( false );
  };

  const checkCanPutSite = () => {
    const requirement = modifySite.name != '' && ( modifySite.sftpPublicKey != '' && selectedSftpStatus == true || selectedSftpStatus == false );
    const isSiteChange: boolean =  modifySite.name != null && modifySite.name != site?.name ||
      modifySite.address != null && modifySite.address != site?.address ||
      modifySite.additionalAddress != null && modifySite.additionalAddress != site?.additionalAddress ||
      modifySite.city != null && modifySite.city != site?.city ||
      modifySite.country != null && modifySite.country != site?.country ||
      modifySite.country != null && modifySite.country != site?.country ||
      modifySite.zipCode != null && modifySite.zipCode != site?.zipCode ||
      modifySite.sftpPublicKey != null && modifySite.sftpPublicKey != site?.sftpPublicKey ||
      defaultSftpStatus != selectedSftpStatus ||
      removedEmails.length > 0 ||
      newEmails.length > 0;

    const isAdminsChange: boolean = changedAdmins.added.length > 0 || changedAdmins.removed.length > 0;
    setCanPut(
      requirement && (
        isSiteChange ||
        isAdminsChange
      )
    );
    setHasChanged(
      isSiteChange ||
      isAdminsChange
    );
  };

  const getCountOfUser = () => {
    const usersId = defaultUsers.map( user => { return user.object.id; } );
    const adminsId =  defaultAdmins.map( admin => { return admin.object.id; } )
      .filter( adminId => { return usersId.find( userId => { return userId == adminId; } ) == null; } );

    const allUsers = new Array<string>()
      .concat( usersId )
      .concat( adminsId.filter( adminId => { return changedAdmins.removed.find( removedAdmin => { return removedAdmin.id == adminId; } ) == null; } ) )
      .concat( changedAdmins.added.map( user => { return user.id; } ) );

    return Array.from( new Set( allUsers ) ).length;
  };

  /*------------------------------- Front -------------------------------*/
  return (
    <>
      <HomeLeftBar items={ NavigationService.getNavBarItems( token, dispatch ) } needConfirmation={ hasChanged } />
      <div className='flex flex-col min-w-[350px] h-full w-full'>
        <HomeTopBar title={ t( 'Modify site' ) } needConfirmationToNav={ hasChanged }/>
        <Breadcrumb
          needConfirmationToNav={ hasChanged }
          links={
            [
              { label: t( 'common:site' ), route: ROUTES.SITES, icon: ( <SitesIcon className='h-[14px] select-none cursor-pointer'/> ) },
              { label: site ? site.name : t( 'Modify site' ), route: ROUTES.SITE.replace( SITE_ID, params.siteId ?? '' ) }
            ]
          }
        />
        {
          !isLoading && site ?
            <div className='flex flex-col items-center justify-center'>
              <form className='relative flex flex-col gap-4 h-full w-full mb-12 items-center justify-center p-4 px-[10%]'>
                <div className='w-full'>
                  <Select
                    required
                    disabled={ true }
                    value={ clientOfSite }
                    label={ t( 'Client' ) }
                    placeholder={ t( 'No associated clients' ) }
                    icon={ <ClientsIcon className='w-7 h-7' /> }
                    options={ [ clientOfSite ] }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    required
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_NAME }
                    label={ t( 'Site name' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Site' ) + '...' }
                    icon={ <SitesIcon className='w-7 h-7' /> }
                    value={ site.name ?? '' }
                    onChange={ newSiteName => {
                      modifySite.name = site?.name != newSiteName ? newSiteName : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ADDRESS }
                    label={ t( 'Address' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Address' ) + '...' }
                    icon={ <AddressIcon className='w-7 h-7' /> }
                    value={ site.address ?? '' }
                    onChange={ newAddress => {
                      modifySite.address = site?.address != newAddress ? newAddress : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ADDITIONAL_ADDRESS }
                    label={ t( 'Additional address' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Additional address' ) + '...' }
                    icon={ <AddressIcon className='w-7 h-7' /> }
                    value={ site.additionalAddress ?? '' }
                    onChange={ newAdditionalAddress => {
                      modifySite.additionalAddress = site?.additionalAddress != newAdditionalAddress ? newAdditionalAddress : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_ZIPCODE }
                    label={ t( 'Zip code' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'Zip code' ) + '...' }
                    icon={ <ZipCodeIcon className='w-7 h-7' /> }
                    value={ site.zipCode ?? '' }
                    onChange={ newZipCode => {
                      modifySite.zipCode = site?.zipCode != newZipCode ? newZipCode : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_CITY }
                    label={ t( 'City' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'City' ) + '...' }
                    icon={ <CityIcon className='w-7 h-7' /> }
                    value={ site.city ?? '' }
                    onChange={ newCity => {
                      modifySite.city = site?.city != newCity ? newCity : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <Input
                    maxLength={ MAX_LENGTH_ENTITY.UF_SITE_COUNTRY }
                    label={ t( 'common:country' ) }
                    disabled={ !isAdminOfSite }
                    placeholder={ t( 'common:country' ) + '...' }
                    icon={ <CountryIcon className='w-7 h-7' /> }
                    value={ site.country ?? '' }
                    onChange={ newCountry => {
                      modifySite.country = site?.country != newCountry ? newCountry : undefined;
                      setModifySite( structuredClone( modifySite ) );
                    } }
                  />
                </div>

                <div className='w-full'>
                  <MultiSelect
                    icon={ <SectorsIcon className='w-7 h-7'/> }
                    label={ t( 'Sector' ) }
                    disabled={ true }
                    placeholder={ t( 'No associated sectors' ) }
                    selectedValues={ CommonGlobalService.toOptions( site.sectors ?? [], false, true ) }
                    getValues={ CommonGlobalService.toOptions( site.sectors ?? [], false, true ) }
                    clearable={ false }
                  />
                </div>

                <div className='flex flex-col w-full gap-2'>


                  <MultiSelect
                    key={ clientOfSite?.value }
                    disabled={ !clientOfSite || !isAdminOfSite }
                    label={ t( 'Administrators' ) + ' (' + selectedAdmins.length + ')' }
                    placeholder={ t( 'No associated users' ) }
                    icon={ <UsersIcon className='w-7 h-7' /> }
                    clearable={ false }
                    selectedValues={ selectedAdmins }
                    getValues={ getUsers }
                    onChange={ options => {
                      options.map( user => {
                        user.object.isAdmin = true;
                        return user;
                      } );
                      setSelectedAdmins( options );
                    } }
                    extra={ selectedValue => {
                      return (
                        <SwitchAdminUser
                          objectId={ selectedValue.value }
                          isDisabled={ true }
                          defaultValue={ selectedValue.object.isAdmin }
                          onChange={ value => {
                            selectedValue.object.isAdmin = value;
                            const findedUser = selectedAdmins.find( item => { return selectedValue.object.id == item.value; } );
                            if ( findedUser ) {
                              findedUser.object.isAdmin = value;
                            }
                            setSelectedAdmins( structuredClone( selectedAdmins ) );
                          } }
                        />
                      );
                    } }
                  />


                  {
                    me && ( CommonGlobalService.isAdminOfSite( me, site.id ) || CommonGlobalService.isAdminOfClient( me, site.clientId ) ) ?
                      <>
                        <div className='flex gap-1.5 px-1 w-full'>
                          { t( 'Total users :' ) }
                          <span className='font-medium underline underline-offset-2'>
                            { String( getCountOfUser() ) }
                          </span>
                        </div>

                        <div className='w-full my-4'>
                          <SftpSiteSection
                            site={ site }
                            modifySite={ modifySite }
                            setModifySite={ setModifySite }
                            sftpStatus={ selectedSftpStatus }
                            setSftpStatus={ setSelectedSftpStatus}
                            setDefaultSftpStatus={ setDefaultSftpStatus }
                            newEmails={ newEmails }
                            setNewEmails={ setNewEmails }
                            removedEmails={ removedEmails }
                            setRemovedEmails={ setRemovedEmails }
                          />
                        </div>
                      </>
                      :
                      null
                  }
                </div>

                <div className='flex justify-center w-full h-14 mt-10'>
                  {
                    isAdminOfSite ?
                      <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                        <Button
                          label={ t( 'Delete' ) }
                          icon={ <BinIcon className='h-[24px] w-[24px] stroke-white'/> }
                          color='#f87171'
                          colorHover='#ef4444'
                          onClick={ () => { setConfirmDeleteModalIsOpen( true ); } }
                        />
                      </div>
                      :
                      null
                  }
                  <div className='h-fit w-1/3 min-w-[160px] max-w-[250px]'>
                    <Button
                      label={ t( 'save' ) }
                      disable={ !canPut }
                      icon={ <SaveIcon className='h-[23px] w-[23px] stroke-white'/> }
                      color='#0bb173'
                      colorHover='#088D5C'
                      onClick={ onSaveClick }
                    />
                  </div>
                </div>
              </form>

              { confirmDeleteModalIsOpen ?
                <ErrorModal
                  title={ t( 'Deletion of {0} !' ).replace( '{0}', site.name )  }
                  isOpen={ confirmDeleteModalIsOpen }
                  onClose={ () => { setConfirmDeleteModalIsOpen( false ); } }
                >
                  <BodyConfirmModal
                    onCancel={ () => { setConfirmDeleteModalIsOpen( false ); } }
                    onConfirm={ async () => {
                      const isDeleted = await deleteSite();
                      if ( isDeleted ) {
                        NavigationService.toSites( navigate, false );
                      }
                      setConfirmDeleteModalIsOpen( false );
                    } }
                  >
                    <span className='flex flex-col w-full h-full text-[14px] font-[475] text-gray-600 p-3'>
                      { t( 'Are you sure you want to delete this site ?' ) }
                    </span>
                  </BodyConfirmModal>
                </ErrorModal>
                :
                null
              }
            </div>
            :
            <Loading/>
        }
      </div>
    </>
  );

}